import {Controller, useForm} from 'react-hook-form'

import CountrySelector from '@/components/CountrySelector/CountrySelector'
import StateProvinceSelector from '@/components/StateProvinceSelector/StateProvinceSelector'
import {isStateProvinceRequiredCountry} from '@/lib/flight/constants'
import type {PaxCountry} from '@/lib/flight/flightModel'
import {isCountryCode} from '@/lib/locale/country'
import {useLocale} from '@/lib/locale/locale'
import {getPhoneInfoByLocale} from '@/lib/locale/phoneNumbers'
import type {ShippingInfo} from '@/lib/payment/paymenModel'

import style from './AddressForm.module.scss'
import AddressAutocomplete from './components/AddressAutocomplete'
import FormField from './components/FormField'

interface AddressFormProps {
  initialAddress?: ShippingInfo
  initialMakeDefault: boolean
}

interface AddressFormData extends ShippingInfo {
  country: PaxCountry | ''
  makeDefault: boolean
}

export interface AddressFormHandle {
  submit: () => Promise<{
    isValid: boolean
    data: ShippingInfo
    makeDefault: boolean
  }>
}

const AddressForm = memo<AddressFormProps & {ref?: Ref<AddressFormHandle>}>(function AddressForm({
  ref,
  initialAddress,
  initialMakeDefault,
}) {
  const defaultValues: AddressFormData = {
    name: initialAddress?.name ?? '',
    phone: initialAddress?.phone ?? '',
    line1: initialAddress?.line1 ?? '',
    line2: initialAddress?.line2 ?? '',
    city: initialAddress?.city ?? '',
    postalCode: initialAddress?.postalCode ?? '',
    state: initialAddress?.state ?? '',
    country: (() => {
      const countryValue = initialAddress?.country ?? ''
      return isCountryCode(countryValue) ? countryValue : ''
    })(),
    makeDefault: initialMakeDefault,
  }

  const {control, watch, trigger, setValue, getValues} = useForm<AddressFormData>({
    mode: 'onTouched',
    defaultValues,
  })

  const [locale] = useLocale()
  const phoneInfo = getPhoneInfoByLocale(locale)
  const phonePrefix = phoneInfo.displayNumber.split(' ')[0]

  const countryValue = watch('country', defaultValues.country)

  useImperativeHandle(
    ref,
    () => ({
      submit: async () => {
        const isFormValid = await trigger(undefined, {shouldFocus: true})
        const formData = getValues()

        return {
          isValid: isFormValid,
          data: {
            name: formData.name,
            phone: formData.phone,
            line1: formData.line1,
            line2: formData.line2,
            city: formData.city,
            postalCode: formData.postalCode,
            state: formData.state,
            country: formData.country,
          } as ShippingInfo,
          makeDefault: formData.makeDefault,
        }
      },
    }),
    [trigger, getValues],
  )

  useEffect(
    function triggerStateValidationOnCountryChange() {
      if (isStateProvinceRequiredCountry(countryValue)) {
        void trigger('state')
      }
    },
    [countryValue, trigger],
  )

  const handleAddressAutoCompleteChange = (updates: {
    line1?: string
    country?: string
    state?: string
    city?: string
    postalCode?: string
  }) => {
    Object.entries(updates).forEach(([key, value]) => {
      setValue(key as keyof AddressFormData, value, {
        shouldValidate: true,
        shouldDirty: true,
        shouldTouch: true,
      })
    })
  }

  return (
    <div className={style.Form}>
      <div className={style.TwoColumn}>
        <FormField<AddressFormData>
          control={control}
          name='name'
          label='Full name'
          placeholder='Input your name here'
          isRequired
          autoComplete='name'
          rules={{required: 'Full name is required'}}
        />

        <FormField<AddressFormData>
          control={control}
          name='phone'
          label='Phone number'
          type='tel'
          placeholder={phonePrefix}
          isRequired
          autoComplete='tel'
          rules={{
            required: 'Phone number is required',
            pattern: {
              value: /^[0-9+\s()-]+$/,
              message: 'Please enter a valid phone number',
            },
          }}
        />
      </div>

      <div className={style.Divider} />

      <div>
        <label htmlFor='dialog-line1'>Address line 1</label>
        <Controller
          control={control}
          name='line1'
          rules={{required: 'Address line 1 is required'}}
          render={({field: {onBlur}, fieldState: {invalid, error}}) => (
            <>
              <div className={`${style.AutocompleteWrapper} ${invalid ? style.InputError : ''}`}>
                <AddressAutocomplete
                  defaultValue={initialAddress?.line1}
                  onChange={handleAddressAutoCompleteChange}
                  onBlur={() => {
                    onBlur()
                    void trigger('line1')
                  }}
                />
              </div>
              {error && <div className={style.ErrorMessage}>{error.message}</div>}
            </>
          )}
        />
      </div>

      <FormField<AddressFormData>
        control={control}
        name='line2'
        label='Address line 2 (optional)'
        placeholder='Input your address'
        autoComplete='address-line2'
      />

      <div className={style.TwoColumn}>
        <FormField<AddressFormData>
          control={control}
          name='city'
          label='City'
          placeholder='Input your city'
          isRequired
          autoComplete='address-level2'
          rules={{required: 'City is required'}}
          id='city'
        />

        <div>
          <Controller
            control={control}
            name='country'
            rules={{required: 'Country is required'}}
            render={({field, fieldState: {invalid, error, isTouched}}) => (
              <CountrySelector
                name='country'
                value={field.value || ''}
                onChange={field.onChange}
                onBlur={field.onBlur}
                isRequired
                label='Country'
                placeholder='United States'
                inputClassName={style.CountryFieldInput}
                inputRef={field.ref}
                isInvalid={invalid}
                errorMessage={isTouched ? error?.message : undefined}
              />
            )}
          />
        </div>
      </div>

      <div className={style.TwoColumn}>
        <FormField<AddressFormData>
          control={control}
          name='postalCode'
          label='ZIP code'
          placeholder='Input your zipcode'
          isRequired
          autoComplete='postal-code'
          rules={{required: 'ZIP code is required'}}
        />

        {isStateProvinceRequiredCountry(countryValue) && (
          <div>
            <Controller
              control={control}
              name='state'
              rules={{required: 'State/Province is required'}}
              render={({
                field: {onChange, onBlur, value},
                fieldState: {invalid, error, isTouched},
              }) => (
                <StateProvinceSelector
                  name='state'
                  countryCode={countryValue}
                  value={value ?? ''}
                  onChange={onChange}
                  onBlur={onBlur}
                  isRequired
                  label='State/Province'
                  placeholder='Select your state/province'
                  inputClassName={style.StateFieldInput}
                  isInvalid={invalid}
                  errorMessage={isTouched ? error?.message : undefined}
                />
              )}
            />
          </div>
        )}
      </div>

      <div className={style.CheckboxRow}>
        <Controller
          control={control}
          name='makeDefault'
          render={({field: {onChange, value}}) => (
            <>
              <input
                type='checkbox'
                id='make-default'
                checked={value}
                onChange={e => {
                  onChange(e.target.checked)
                }}
                onKeyDown={e => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    onChange(!value)
                  }
                }}
                aria-label='Make this the default address'
              />
              <label htmlFor='make-default'>Make it as default address</label>
            </>
          )}
        />
      </div>
    </div>
  )
})

export default AddressForm
